import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  updateProfileSchema,
  LOCALE_OPTIONS,
  TIMEZONE_OPTIONS,
  type UpdateProfileFormValues,
} from '../schema/myProfileSchema';
import { useUpdateMyProfile } from '../hooks/useUpdateMyProfile';
import type { MyProfileResponse, ApiError } from '../types/myProfile';

interface BasicInfoTabProps {
  profile: MyProfileResponse;
  onSuccess?: (msg: string) => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  outline: 'none',
  boxSizing: 'border-box',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: 'var(--color-danger)',
};

export function BasicInfoTab({ profile, onSuccess }: BasicInfoTabProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>();

  const mutation = useUpdateMyProfile();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
    watch,
  } = useForm<UpdateProfileFormValues>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      displayName: profile.displayName,
      preferredLocale: profile.preferredLocale,
      preferredTimezone: profile.preferredTimezone,
    },
  });

  // Reset form when profile data changes externally
  useEffect(() => {
    reset({
      displayName: profile.displayName,
      preferredLocale: profile.preferredLocale,
      preferredTimezone: profile.preferredTimezone,
    });
  }, [profile.displayName, profile.preferredLocale, profile.preferredTimezone, reset]);

  const _watchedLocale = watch('preferredLocale');
  void _watchedLocale; // used for reactivity (locale change triggers re-render)

  const handleCancel = () => {
    reset({
      displayName: profile.displayName,
      preferredLocale: profile.preferredLocale,
      preferredTimezone: profile.preferredTimezone,
    });
    setEditing(false);
    setServerError(undefined);
  };

  const onSubmit = handleSubmit((values) => {
    setServerError(undefined);
    mutation.mutate(
      {
        displayName: values.displayName,
        preferredLocale: values.preferredLocale,
        preferredTimezone: values.preferredTimezone,
      },
      {
        onSuccess: () => {
          setEditing(false);
          onSuccess?.(t('myProfile.toast.updated'));
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          if (apiErr.code === 'PROFILE-422-001') {
            setServerError(t('myProfile.error.validationFailed'));
          } else {
            setServerError(apiErr.message ?? t('myProfile.toast.updateFailed'));
          }
        },
      }
    );
  });

  const btnPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnSecondary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-surface-elevated)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--spacing-6)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
          }}
        >
          {t('myProfile.basic.title')}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          {isDirty && editing && (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                padding: '2px var(--spacing-2)',
                borderRadius: 'var(--radius-full)',
                background: 'color-mix(in srgb, var(--color-warning) 18%, transparent)',
                color: 'var(--color-warning)',
                border: '1px solid var(--color-warning)',
              }}
            >
              {t('myProfile.basic.dirtyBadge')}
            </span>
          )}
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              style={btnSecondary}
              aria-label={t('myProfile.basic.editButton')}
            >
              {t('myProfile.basic.editButton')}
            </button>
          )}
        </div>
      </div>

      {serverError && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            marginBottom: 'var(--spacing-4)',
            background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-muted))',
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-danger)',
          }}
        >
          {serverError}
        </div>
      )}

      <form
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        aria-label={t('myProfile.basic.formAriaLabel')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          {/* Display Name */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
            <label
              htmlFor="displayName"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
              }}
            >
              {t('myProfile.basic.displayName.label')}
            </label>
            {editing ? (
              <>
                <input
                  id="displayName"
                  type="text"
                  placeholder={t('myProfile.basic.displayName.placeholder')}
                  aria-invalid={!!errors.displayName}
                  aria-describedby={errors.displayName ? 'displayName-error' : undefined}
                  {...register('displayName')}
                  style={errors.displayName ? inputErrorStyle : inputStyle}
                />
                {errors.displayName && (
                  <span
                    id="displayName-error"
                    role="alert"
                    style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
                  >
                    {errors.displayName.message}
                  </span>
                )}
              </>
            ) : (
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                {profile.displayName}
              </span>
            )}
          </div>

          {/* Language */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
            <label
              htmlFor="preferredLocale"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
              }}
            >
              {t('myProfile.basic.locale.label')}
            </label>
            {editing ? (
              <>
                <select
                  id="preferredLocale"
                  aria-invalid={!!errors.preferredLocale}
                  aria-describedby={errors.preferredLocale ? 'locale-error' : undefined}
                  {...register('preferredLocale')}
                  style={errors.preferredLocale ? inputErrorStyle : inputStyle}
                >
                  {LOCALE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {errors.preferredLocale && (
                  <span
                    id="locale-error"
                    role="alert"
                    style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
                  >
                    {errors.preferredLocale.message}
                  </span>
                )}
              </>
            ) : (
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                {profile.preferredLocale === 'ko' ? t('myProfile.identity.localeLabelKo') : 'English'}
              </span>
            )}
          </div>

          {/* Timezone */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
            <label
              htmlFor="preferredTimezone"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
              }}
            >
              {t('myProfile.basic.timezone.label')}
            </label>
            {editing ? (
              <>
                <select
                  id="preferredTimezone"
                  aria-invalid={!!errors.preferredTimezone}
                  aria-describedby={errors.preferredTimezone ? 'timezone-error' : undefined}
                  {...register('preferredTimezone')}
                  style={errors.preferredTimezone ? inputErrorStyle : inputStyle}
                >
                  {TIMEZONE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {errors.preferredTimezone && (
                  <span
                    id="timezone-error"
                    role="alert"
                    style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
                  >
                    {errors.preferredTimezone.message}
                  </span>
                )}
              </>
            ) : (
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                {profile.preferredTimezone}
              </span>
            )}
          </div>

          {/* Action buttons */}
          {editing && (
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', paddingTop: 'var(--spacing-2)' }}>
              <button
                type="submit"
                disabled={!isDirty || isSubmitting || mutation.isPending}
                style={{
                  ...btnPrimary,
                  opacity: !isDirty || isSubmitting || mutation.isPending ? 0.6 : 1,
                  cursor: !isDirty || isSubmitting || mutation.isPending ? 'not-allowed' : 'pointer',
                }}
                aria-busy={mutation.isPending}
              >
                {mutation.isPending ? t('myProfile.basic.saving') : t('myProfile.basic.saveButton')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting || mutation.isPending}
                style={btnSecondary}
              >
                {t('myProfile.basic.cancelButton')}
              </button>
            </div>
          )}
        </div>
      </form>

      {/* Read-only: email (not editable) */}
      <div
        style={{
          marginTop: 'var(--spacing-6)',
          paddingTop: 'var(--spacing-4)',
          borderTop: '1px solid var(--color-surface-border)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
            }}
          >
            {t('myProfile.basic.email.label')}
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            {profile.email}
          </span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            {t('myProfile.basic.email.readOnly')}
          </span>
        </div>
      </div>
    </div>
  );
}
